import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, Circle, Tooltip, ZoomControl, Pane, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import io from 'socket.io-client';
import { apiOrigin } from '../apiOrigin';
import api from '../services/api';
import {
  Activity,
  AlertTriangle,
  Building2,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Search,
  Layers,
  MapPin,
  RefreshCw,
  Route,
  User,
  Wifi,
  WifiOff,
} from 'lucide-react';
import './LiveMap.css';
import PageHeader from '../components/PageHeader';
import GlidingMarker, { rideGeometry } from '../components/GlidingMarker';
import { readRideStops } from '../utils/rideStops';
import { uhrzeit } from '../lib/zeit';
import { carMarkerSvg, isCompactCarSize, carSizeForZoom } from '../lib/carMarker';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Betriebsregion (Duisburg/Ruhrgebiet, = App-Default): die Karte startet IMMER hier.
const OPERATING_CENTER = [51.4344, 6.7623];
const OPERATING_ZOOM = 11;

// LIVEKARTE-UBER-001: helle Karte (Vorbild Flottenportal des Marktführers) — gleich wie das Partnerportal.
const MAP_STYLE = 'navigation-day-v1';
export const LIVE_EVENT = 'live_map_position';

const CLASS_LABELS = {
  standard: 'VOOGO Basis',
  comfort: 'VOOGO Komfort',
  premium: 'VOOGO Business',
  xl: 'VOOGO Family',
  max: 'VOOGO Van',
  taxi: 'VOOGO Taxi',
};

// Farbvertrag (Owner 19.09.2026, ersetzt den vom 04.09.): die Farbe sagt, WOHIN es geht —
//   blau = zum Abholort (Auto „Zum Abholort" + Linie zum Abholort) ·
//   grün = zum Ziel (Auto „Mit Fahrgast" + Linie zum Ziel) · grau = frei (Owner 08.10.2026, vorher orange) · hellgrau = offline.
// Ein Auto trägt die Farbe seiner Linie als Schein unter dem weißen Wagen. Dieselben Hexwerte im Partnerportal
// (partner-portal/src/pages/LiveMap.js), in Legende, Filtern, Liste, Popups und Folgefahrt-Hinweisen.
// Rot-Grün-Schwäche: Grün ist dunkel, Grau neutral, Blau/Grün trennt der Farbton
// (die Blau-Gelb-Achse bleibt erhalten) und zusätzlich das Linienmuster.
const PHASE_COLOR = { free: '#6b7280', pickup: '#2563eb', pax: '#15803d' };
// Schrift in Phasenfarbe (≥ 4,5:1 auf Weiß, design-system/MASTER.md): Grau als Schrift eine Stufe dunkler.
const PHASE_TEXT = { free: '#374151', pickup: '#2563eb', pax: '#15803d' };
const PHASE_LABEL = { free: 'Frei', pickup: 'Zum Abholort', pax: 'Mit Fahrgast' };
// Auswahl = Ink-Ring (MASTER.md: Ink = aktive Zustände), nie Blau — Blau heißt „zum Abholort".
const SELECT_RING = '#0a0a0a';
// Linien: Farbe = wohin (blau Abholort, grün Ziel), Muster = Stand. Gestrichelt = Anfahrt zum Abholort,
// durchgezogen = mit Fahrgast zum Ziel, gepunktet + heller = geplanter Ziel-Abschnitt (Suche, Angenommen/
// Angekommen vor dem Abholen, gespeicherte Strecke mit Zwischenstopps). Auswahl: dicker + deckender, Farbe bleibt.
const LINE_STYLE = {
  pickup: { color: PHASE_COLOR.pickup, dashArray: '8 6', opacity: 0.9, label: 'Zum Abholort' },
  dropoff: { color: PHASE_COLOR.pax, dashArray: null, opacity: 0.9, label: 'Zum Ziel · mit Fahrgast' },
  planned: { color: PHASE_COLOR.pax, dashArray: '1 8', opacity: 0.7, label: 'Zum Ziel · geplant' },
};
const linePathOptions = (kind, selected = false) => {
  const style = LINE_STYLE[kind] || LINE_STYLE.planned;
  return {
    color: style.color,
    weight: selected ? 6 : 4,
    opacity: selected ? Math.min(1, style.opacity + 0.15) : style.opacity,
    dashArray: style.dashArray,
    lineCap: 'round',
    lineJoin: 'round',
  };
};
// Fahrtstatus-Chip der Fahrtkarte: Design-System-Palette (ui/kit.js statusColor, „Unterwegs" = Violett) —
// bewusst NICHT der Phasen-Farbvertrag; färbt keine Linie mehr.
const statusLabel = { searching: 'Suche', accepted: 'Angenommen', arrived: 'Angekommen', in_progress: 'Unterwegs' };
const statusColor = { searching: '#4b5563', accepted: '#4b5563', arrived: '#4b5563', in_progress: '#9c27b0' };

const bearingDeg = (a, b) => {
  const radians = (value) => value * Math.PI / 180;
  const y = Math.sin(radians(b.lng - a.lng)) * Math.cos(radians(b.lat));
  const x = Math.cos(radians(a.lat)) * Math.sin(radians(b.lat))
    - Math.sin(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.cos(radians(b.lng - a.lng));
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

// Karten-Auto „V4-kräftig" (Owner-Freigabe 08.10.2026): weißes Top-Down-Auto aus src/lib/carMarker.js, gedreht in
// Fahrtrichtung, Statusfarbe als Schein darunter. Ausgewählter Fahrer (Klick auf Auto, Liste oder Fahrtkarte)
// bekommt zusätzlich den Ink-Auswahlring (SELECT_RING) — der Statusschein behält seine Phasenfarbe.
// data-phase-color trägt die Phasenfarbe maschinenlesbar (Tests, Werkzeuge); die Optik kommt aus dem SVG.
const carIcon = (phase, size, bearing = 0, selected = false) => {
  const color = PHASE_COLOR[phase] || PHASE_COLOR.free;
  const ring = selected
    ? `<div class="admin-car-select-ring" style="position:absolute;inset:0;border-radius:50%;border:3px solid ${SELECT_RING};box-shadow:0 0 0 6px ${SELECT_RING}2e;"></div>`
    : '';
  return new L.DivIcon({
    html: `<div class="admin-map-car" data-phase="${phase}" data-phase-color="${color}" style="position:relative;width:${size}px;height:${size}px;">${ring}
      <div style="position:absolute;inset:0;transform:rotate(${bearing}deg);transform-origin:center;">${carMarkerSvg(color, isCompactCarSize(size))}</div>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
    className: '',
  });
};

function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  return null;
}

// Fliegt zum ausgewählten Fahrer (Klick auf eine Fahrtkarte) — nie automatisch, nie beim Nachladen.
// Owner 17.10.2026: derselbe Klick noch einmal wählt ab und fliegt zurück auf die Ansicht VOR dem Hineinzoomen
// (kind 'back'). Die Ansicht wird beim ersten Hineinfliegen gemerkt; ein Wechsel A → B behält sie.
function FlyTo({ target }) {
  const map = useMap();
  const savedView = useRef(null);
  useEffect(() => {
    if (!target || !map || typeof map.flyTo !== 'function') return;
    if (target.kind === 'back') {
      if (savedView.current) map.flyTo(savedView.current.center, savedView.current.zoom, { duration: 0.8 });
      savedView.current = null;
      return;
    }
    const currentZoom = typeof map.getZoom === 'function' ? map.getZoom() : OPERATING_ZOOM;
    if (!savedView.current) {
      const c = typeof map.getCenter === 'function' ? map.getCenter() : null;
      savedView.current = { center: c ? [c.lat, c.lng] : OPERATING_CENTER, zoom: currentZoom };
    }
    map.flyTo([target.lat, target.lng], Math.max(currentZoom, 14), { duration: 0.8 });
  }, [target, map]);
  return null;
}

function DriverAvatar({ name, avatar, size = 28 }) {
  const [broken, setBroken] = useState(false);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <span className="admin-map-avatar" style={{ width: size, height: size }}>
      {avatar && !broken ? <img src={avatar} alt="" onError={() => setBroken(true)} /> : <b>{initial}</b>}
    </span>
  );
}

// ── BETRIEB-SENTRY-KLEINFUNDE-001 · Teil A ───────────────────────────────────
// Diese Seite erzeugte den Sentry-Vorfall 140571743 NICHT — sie holt beide Quellen über
// `Promise.allSettled` und kann bauartbedingt keine unbehandelte Ablehnung werfen. Was ihr
// fehlte, war die GRENZE: bei einem Ausfall lief das 8-Sekunden-Nachladen unbegrenzt weiter
// und machte aus einem kurzen Ausfall eine Dauerlast auf dem eigenen Server.
// Jetzt hält das Nachladen nach MAX_FEHLVERSUCHE Fehlschlägen in Folge an; „Aktualisieren"
// ist der Weg zurück. Eine abgelaufene/unzureichende Anmeldung (401/403) ist kein Netzfehler
// und läuft nie in die Wiederholschleife.
const NACHLADE_INTERVALL_MS = 8000;
const MAX_FEHLVERSUCHE = 5;

const driverPhase = (driver) => driver.ride_status === 'in_progress'
  ? 'pax'
  : (driver.ride_status === 'accepted' || driver.ride_status === 'arrived') ? 'pickup' : 'free';

// FAHRER-ANWESENHEIT-HINTERGRUND-001: Klartext zum App-Zustand je Online-Fahrer — NUR im Admin.
// Ohne Signal (App ohne den neuen Vertrag) keine Behauptung: „unbekannt". Wer das liest, ist der
// Nachweis, dass das Hintergrund-Signal wirkt.
const minutesSince = (ms) => Math.max(0, Math.round((Date.now() - Number(ms)) / 60000));
const clock = (ms) => uhrzeit(Number(ms));
export const presenceLabel = (presence) => {
  if (!presence) return null;
  if (presence.app_state === 'background') return `App im Hintergrund seit ${minutesSince(presence.background_since_ms)} min`;
  if (presence.app_state === 'foreground') return 'App im Vordergrund';
  return 'App-Zustand unbekannt (alte App)';
};
export const presenceDetails = (presence) => {
  if (!presence) return [];
  const parts = [];
  if (presence.confirm_deadline_ms) {
    parts.push(`Pflicht-Tap läuft bis ${clock(presence.confirm_deadline_ms)} Uhr${presence.confirm_reason === 'background' ? ' (wegen Hintergrund)' : ''}`);
  }
  if (presence.still_shadow_since_ms) {
    parts.push(`Stillstand + Hintergrund seit ${minutesSince(presence.still_shadow_since_ms)} min (nur Schatten${presence.still_shadow_result === 'would_offline' ? ', wäre offline gegangen' : ''})`);
  }
  return parts;
};

// ADMIN-LIVEKARTE-HEATMAP-001: wählbare Ebene über der Karte (Owner 17.10.2026).
//  · „Nachfrage" (Standard): wo Kunden Preise angefragt haben, als H3-Waben — Größe folgt dem Zoom
//    (Stadt H7 · Stadtteil H8 · Straße H9, Tabelle kommt vom Server). Feste Farben stehen für die
//    Kundenanzahl 1–14 und 15+; Antippen zeigt alle echten Zahlen inkl. wartender Fahrten und
//    freier Fahrer. Ersetzt die früheren 3-km-Kreise „Wartende Anfragen" (stecken jetzt in jeder Wabe).
//  · „Fahrer-Heatmap": genau die Wolke der Fahrer-App (ohne persönliche Anker-Wolke).
export const HEAT_LAYERS = [
  { id: 'demand', label: 'Nachfrage' },
  { id: 'driver', label: 'Fahrer-Heatmap' },
  { id: 'off', label: 'Aus' },
];
const HEAT_LAYER_KEY = 'admin_livemap_heat_layer';
const SEARCH_WINDOW_KEY = 'admin_livemap_search_window';
const SEARCH_WINDOWS = [5, 15, 30, 60];
export const CLOUD_LEVEL = {
  3: { fill: '#facc15', opacity: 0.5, label: 'Nachfrage zieht an' },
  4: { fill: '#f97316', opacity: 0.58, label: 'hohe Nachfrage' },
  5: { fill: '#dc2626', opacity: 0.66, label: 'sehr hohe Nachfrage' },
};
export const DEMAND_COLORS = Object.freeze([
  '#facc15', '#fbb533', '#f99a21', '#f97316', '#ec591b',
  '#e43e22', '#dc2626', '#d82552', '#ca2679', '#b82ca0',
  '#a32eb8', '#8c2cba', '#7329a8', '#5e2390', '#481873',
]);
export const demandColor = (searches) => Number.isSafeInteger(searches) && searches > 0
  ? DEMAND_COLORS[Math.min(searches, DEMAND_COLORS.length) - 1] : null;
const CLOUD_RADIUS_M = 1400;   // 2-km-Zelle der Fahrer-Wolke; Nachbarzellen fließen zu einer Wolke zusammen
const DEFAULT_ZOOM_RESOLUTION = [{ max_zoom: 11, resolution: 7 }, { max_zoom: 13, resolution: 8 }, { max_zoom: null, resolution: 9 }];
export const resolutionForZoom = (zoom, table = DEFAULT_ZOOM_RESOLUTION) => {
  const rows = Array.isArray(table) && table.length ? table : DEFAULT_ZOOM_RESOLUTION;
  const hit = rows.find((row) => row.max_zoom == null || zoom <= row.max_zoom);
  return (hit || rows[rows.length - 1]).resolution;
};
export const demandHexesForResolution = (demand, resolution) => {
  if (!demand) return [];
  const plan = demand.display?.by_resolution?.[resolution];
  if (Array.isArray(plan)) {
    const cells = new Map([7, 8, 9].flatMap((r) => (demand.resolutions?.[r] || []).map((hex) => [`${r}:${hex.id}`, hex])));
    return [...new Set(plan.map(({ resolution: r, id }) => `${r}:${id}`))].map((key) => cells.get(key)).filter(Boolean);
  }
  // Older servers provide no local overlap plan. A single coarser layer is
  // safe; combining H3 logical parents/children on the client would overlap.
  const legacyResolution = resolution === 9 && demand.hidden_h9 > 0 ? 8 : resolution;
  return demand.resolutions?.[legacyResolution] || [];
};
const readStored = (key, allowed, fallback) => {
  try { const v = localStorage.getItem(key); return allowed.includes(v) ? v : fallback; } catch { return fallback; }
};
const writeStored = (key, value) => { try { localStorage.setItem(key, String(value)); } catch { /* privat/gesperrt */ } };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const hexLines = (hex) => [
  plural(hex.searches, 'Kunde suchte', 'Kunden suchten'),
  `${hex.booked} gebucht`,
  ...(hex.reservations > 0 ? [plural(hex.reservations, 'Reservierung', 'Reservierungen')] : []),
  `${hex.waiting} ${hex.waiting === 1 ? 'wartet' : 'warten'} jetzt auf einen Fahrer`,
  plural(hex.free_drivers, 'freier Fahrer', 'freie Fahrer') + ' in der Wabe',
];

// Abholort = blauer Punkt (Farbe der Linie zum Abholort) · Ziel = schwarzes Quadrat (gleich wie das Partnerportal).
const pickupIcon = new L.DivIcon({
  html: `<div style="width:12px;height:12px;background:${PHASE_COLOR.pickup};border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.25)"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  className: '',
});

const dropoffIcon = new L.DivIcon({
  html: '<div style="width:12px;height:12px;background:#0a0a0a;border:3px solid #fff;border-radius:2px;box-shadow:0 0 0 1px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.25)"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  className: '',
});

const stopIcon = (number) => new L.DivIcon({
  html: `<div class="admin-map-stop-marker">${number}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  className: '',
});

function RideRouteDetails({ ride }) {
  const { stops, unreadable } = readRideStops(ride.stops);
  const feePresent = (typeof ride.stop_fee === 'number' || typeof ride.stop_fee === 'string')
    && String(ride.stop_fee).trim() !== '';
  const fee = feePresent ? Number(ride.stop_fee) : NaN;
  const showFee = Number.isFinite(fee) && fee >= 0 && (fee > 0 || stops.length > 0);
  return (
    <div className="admin-map-route">
      <span><i className="is-pickup" />{ride.pickup_address || 'Abholort nicht verfügbar'}</span>
      {stops.map((stop) => (
        <span className="admin-map-stop-row" key={stop.number}>
          <i className="admin-map-stop-number">{stop.number}</i>
          <b>Zwischenstopp {stop.number}: {stop.label}{!stop.position && stop.label !== 'Position nicht verfügbar' ? ' · Position nicht verfügbar' : ''}</b>
        </span>
      ))}
      {unreadable && <span className="admin-map-stops-warning"><AlertTriangle size={14} />Zwischenstopps nicht lesbar</span>}
      <span><i className="is-dropoff" />{ride.dropoff_address || 'Ziel nicht verfügbar'}</span>
      {(stops.length > 0 || unreadable) && <small className="admin-map-route-note">Geplante Zwischenstopps · Fortschritt nicht verfügbar</small>}
      {(unreadable || stops.some((stop) => !stop.position)) && <small className="admin-map-stops-warning">Geplante Strecke nicht vollständig darstellbar</small>}
      {showFee && <small className="admin-map-stop-fee">{fee.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € Stoppzuschlag im Fahrpreis enthalten</small>}
    </div>
  );
}

// `color` färbt Symbol + Rahmen, `textColor` die Schrift im aktiven Zustand (Orange ist als Schrift zu hell).
function MapFilter({ active, color, textColor, icon: Icon, label, value, onClick }) {
  return (
    <button
      type="button"
      className={`admin-map-filter ${active ? 'is-active' : ''}`}
      style={{ '--filter-color': color, '--filter-text': textColor || color }}
      onClick={onClick}
    >
      <Icon size={15} strokeWidth={1.8} />
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function ActiveRideCard({ ride, selected, onSelect }) {
  const price = ride.price == null ? '–' : `${Number(ride.price).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const clickable = !!(ride.driver_id && onSelect);
  return (
    <article
      className={`admin-map-ride-card ${selected ? 'is-selected' : ''} ${clickable ? 'is-clickable' : ''}`}
      data-testid="active-ride-card"
      data-selected={selected ? '1' : '0'}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onSelect(ride) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(ride); } } : undefined}
    >
      <div className="admin-map-ride-top">
        <span className="admin-map-ride-status" style={{ color: statusColor[ride.status] || '#4b5563' }}>
          <i style={{ background: statusColor[ride.status] || '#4b5563' }} />
          {statusLabel[ride.status] || ride.status}
        </span>
        <strong>{price}</strong>
      </div>
      <div className="admin-map-ride-people">
        <span><User size={13} /> {ride.customer_name}</span>
        <span>{ride.driver_name ? <DriverAvatar name={ride.driver_name} avatar={ride.driver_avatar} size={22} /> : <Car size={13} />} {ride.driver_name || 'Noch nicht zugewiesen'}</span>
      </div>
      <RideRouteDetails ride={ride} />
      <footer>
        <span>{CLASS_LABELS[ride.ride_type] || ride.ride_type || 'Fahrzeugklasse offen'}</span>
        <span>#{String(ride.id).slice(0, 8)}</span>
      </footer>
    </article>
  );
}

export default function LiveMap() {
  const [drivers, setDrivers] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [zoom, setZoom] = useState(OPERATING_ZOOM);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState('');
  const [angehalten, setAngehalten] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(true);
  const [sideTab, setSideTab] = useState('drivers');   // Seitenspalte: 'drivers' | 'rides'
  const [presenceSummary, setPresenceSummary] = useState(null);   // FAHRER-ANWESENHEIT-HINTERGRUND-001
  const [heatLayer, setHeatLayer] = useState(() => readStored(HEAT_LAYER_KEY, HEAT_LAYERS.map((l) => l.id), 'demand'));
  const [searchWindow, setSearchWindow] = useState(() => Number(readStored(SEARCH_WINDOW_KEY, SEARCH_WINDOWS.map(String), '60')));
  const [liveHeat, setLiveHeat] = useState(null);
  const [liveHeatError, setLiveHeatError] = useState(false);
  const heatLayerRef = useRef(heatLayer);
  const searchWindowRef = useRef(searchWindow);
  const heatRequestRef = useRef(0);
  const heatAppliedRef = useRef(0);
  const heatEpochRef = useRef(0);
  const heatActiveRef = useRef(false);
  const heatSelectionRef = useRef({ layer: heatLayer, window: searchWindow });
  heatLayerRef.current = heatLayer;
  searchWindowRef.current = searchWindow;
  const socketRef = useRef(null);
  const surfaceRef = useRef(null);
  const prevPosRef = useRef({});
  const bearingRef = useRef({});
  const fehlversucheRef = useRef(0);   // aufeinanderfolgende Fehlschläge (Erfolg setzt zurück)

  const withBearing = useCallback((list) => list.map((driver) => {
    if (driver.current_lat && driver.current_lng) {
      const previous = prevPosRef.current[driver.id];
      if (previous && (Math.abs(previous.lat - driver.current_lat) > 1e-6 || Math.abs(previous.lng - driver.current_lng) > 1e-6)) {
        bearingRef.current[driver.id] = bearingDeg(previous, { lat: driver.current_lat, lng: driver.current_lng });
      }
      prevPosRef.current[driver.id] = { lat: driver.current_lat, lng: driver.current_lng };
    }
    // Kurs: vom Server (current_heading) — die Zwei-Punkte-Rechnung bleibt nur Rückfall für Altbestände.
    const serverHeading = Number(driver.current_heading);
    return { ...driver, bearing: Number.isFinite(serverHeading) && driver.current_heading != null ? serverHeading : (bearingRef.current[driver.id] || 0) };
  }), []);

  // GET /admin/live-heat nur, wenn eine Ebene gewählt ist; Fehler hier zählen nicht als Kartenausfall.
  const loadLiveHeat = useCallback(async () => {
    const layer = heatLayerRef.current;
    const window = searchWindowRef.current;
    if (!heatActiveRef.current || (layer !== 'driver' && layer !== 'demand')) return;
    const request = ++heatRequestRef.current;
    const epoch = heatEpochRef.current;
    // A pending newer poll does not invalidate a finite response for the same selection.
    const isCurrent = () => heatActiveRef.current && epoch === heatEpochRef.current && request > heatAppliedRef.current
      && layer === heatLayerRef.current && window === searchWindowRef.current;
    try {
      const res = await api.get('/admin/live-heat', { params: { window } });
      if (!isCurrent()) return;
      heatAppliedRef.current = request;
      setLiveHeat(res.data || null);
      setLiveHeatError(false);
    } catch {
      if (isCurrent()) {
        heatAppliedRef.current = request;
        setLiveHeatError(true);
      }
    }
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    const [mapResult] = await Promise.allSettled([
      api.get('/admin/livemap'),
      loadLiveHeat(),
    ]);

    if (mapResult.status === 'fulfilled') {
      setDrivers(withBearing(mapResult.value.data.drivers || []));
      setActiveRides(mapResult.value.data.active_rides || []);
      setPresenceSummary(mapResult.value.data.presence || null);
      setLastUpdate(new Date());
      fehlversucheRef.current = 0;
      setError('');
      setSessionError('');
    } else {
      const status = mapResult.reason && mapResult.reason.response && mapResult.reason.response.status;
      if (status === 401 || status === 403) {
        // Abgelaufene/unzureichende Anmeldung: kein Netzfehler, keine Wiederholung.
        fehlversucheRef.current = 0;
        setError('');
        setSessionError(status === 401
          ? 'Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.'
          : 'Dein Zugang reicht für die Live-Karte nicht aus. Bitte melde dich neu an.');
        setAngehalten(true);
      } else {
        fehlversucheRef.current += 1;
        const erschoepft = fehlversucheRef.current >= MAX_FEHLVERSUCHE;
        setError(erschoepft
          ? `Die Live-Kartendaten konnten ${MAX_FEHLVERSUCHE}-mal in Folge nicht geladen werden. Das automatische Nachladen ist angehalten.`
          : 'Die Live-Kartendaten konnten nicht aktualisiert werden.');
        if (erschoepft) setAngehalten(true);
      }
    }

    setLoading(false);
    if (!silent) setRefreshing(false);
  }, [withBearing, loadLiveHeat]);

  // Angehalten heißt: kein Timer, kein Socket, kein Aufruf. Erst „Aktualisieren" startet wieder.
  const erneutVersuchen = useCallback(() => {
    fehlversucheRef.current = 0;
    if (angehalten) setAngehalten(false);   // Effekt startet Timer + Socket neu
    else load();
  }, [angehalten, load]);

  useEffect(() => {
    if (angehalten) return undefined;
    heatActiveRef.current = true;
    load();
    const interval = setInterval(() => load({ silent: true }), NACHLADE_INTERVALL_MS);

    const socket = io(apiOrigin(), {
      auth: { token: localStorage.getItem('admin_token') },
      extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
    });
    socketRef.current = socket;
    socket.on('disconnect', () => setSocketConnected(false));
    // LIVEKARTE-UBER-001: Der Server sendet Live-Positionen in den berechtigten Admin-Raum (join_livemap,
    // gescopt wie GET /admin/livemap). Das alte `driver_location_update` hat der Server NIE gesendet.
    const join = () => { if (typeof socket.emit === 'function') socket.emit('join_livemap'); };
    socket.on('connect', () => { setSocketConnected(true); join(); });
    join();
    socket.on(LIVE_EVENT, (d) => {
      if (!d || !d.driver_id || !Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return;
      const { driver_id, lat, lng } = d;
      const previous = prevPosRef.current[driver_id];
      if (Number.isFinite(d.heading)) bearingRef.current[driver_id] = d.heading;
      else if (previous && (Math.abs(previous.lat - lat) > 1e-6 || Math.abs(previous.lng - lng) > 1e-6)) {
        bearingRef.current[driver_id] = bearingDeg(previous, { lat, lng });
      }
      prevPosRef.current[driver_id] = { lat, lng };
      setDrivers((current) => current.map((driver) => driver.id === driver_id
        ? { ...driver, current_lat: lat, current_lng: lng, current_heading: Number.isFinite(d.heading) ? d.heading : driver.current_heading, bearing: bearingRef.current[driver_id] || driver.bearing || 0 }
        : driver));
    });

    return () => {
      heatActiveRef.current = false;
      heatEpochRef.current += 1;
      clearInterval(interval);
      socket.disconnect();
    };
  }, [load, angehalten]);

  // Ebene oder Zeitfenster gewechselt → sofort nachladen (nicht erst beim nächsten 8-Sekunden-Takt).
  useEffect(() => {
    writeStored(HEAT_LAYER_KEY, heatLayer);
    writeStored(SEARCH_WINDOW_KEY, searchWindow);
    const previous = heatSelectionRef.current;
    heatSelectionRef.current = { layer: heatLayer, window: searchWindow };
    if (previous.layer === heatLayer && previous.window === searchWindow) return;
    heatEpochRef.current += 1;
    setLiveHeat(null);
    setLiveHeatError(false);
    if (!angehalten) loadLiveHeat();
  }, [heatLayer, searchWindow]);   // bewusst nur bei Wechsel — der 8-Sekunden-Takt lädt über load() nach

  const cloudZones = heatLayer === 'driver' && liveHeat ? (liveHeat.driver?.zones || []) : [];
  const demand = heatLayer === 'demand' && liveHeat && !liveHeatError ? liveHeat.demand : null;
  const hexResolution = resolutionForZoom(zoom, demand?.zoom_resolution);
  const hexes = demandHexesForResolution(demand, hexResolution);
  const demandTotals = demand?.totals;

  const onlineDrivers = drivers.filter((driver) => driver.is_online);
  const filteredDrivers = onlineDrivers.filter((driver) => filter === 'all' || driverPhase(driver) === filter);
  const freeCount = onlineDrivers.filter((driver) => driverPhase(driver) === 'free').length;
  const pickupCount = onlineDrivers.filter((driver) => driverPhase(driver) === 'pickup').length;
  const paxCount = onlineDrivers.filter((driver) => driverPhase(driver) === 'pax').length;
  const carSize = carSizeForZoom(zoom);
  const driverById = Object.fromEntries(drivers.map((driver) => [driver.id, driver]));
  const rideById = Object.fromEntries(activeRides.map((ride) => [ride.id, ride]));
  const rideByDriver = Object.fromEntries(drivers
    .filter((driver) => driver.active_ride_id && rideById[driver.active_ride_id])
    .map((driver) => [driver.id, rideById[driver.active_ride_id]]));
  const followupsByDriver = activeRides.reduce((result, ride) => {
    if (!ride.driver_id || ride.status !== 'accepted' || ride.id === driverById[ride.driver_id]?.active_ride_id) return result;
    if (!result[ride.driver_id]) result[ride.driver_id] = [];
    result[ride.driver_id].push(ride);
    return result;
  }, {});
  const flyToDriver = (driver) => {
    if (driver && driver.current_lat && driver.current_lng) setFlyTarget({ kind: 'focus', lat: driver.current_lat, lng: driver.current_lng, at: Date.now() });
  };
  // Zweiter Klick auf dieselbe Auswahl (Liste oder Fahrtkarte): abwählen und zurück auf die vorige Ansicht.
  const toggleDriver = (driverId) => {
    if (selectedDriverId === driverId) {
      setSelectedDriverId(null);
      setFlyTarget({ kind: 'back', at: Date.now() });
      return;
    }
    setSelectedDriverId(driverId);
    flyToDriver(driverById[driverId]);
    // Schmaler Bildschirm: Liste liegt unter der Karte — zur Karte scrollen, damit das Hineinzoomen sichtbar ist.
    const el = surfaceRef.current;
    if (el && typeof el.scrollIntoView === 'function' && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 900px)').matches) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const selectRide = (ride) => {
    if (!ride.driver_id) return;
    toggleDriver(ride.driver_id);
  };
  const selectDriver = (driver) => toggleDriver(driver.id);
  // Fahrerliste (Owner 04.09.): NUR Online-Fahrer, dem Toolbar-Filter folgend, per Name durchsuchbar.
  const listedDrivers = filteredDrivers.filter((driver) => (driver.name || '').toLowerCase().includes(search.toLowerCase()));
  const vehicleLine = (driver) => [driver.license_plate, [driver.vehicle_brand, driver.vehicle_model].filter(Boolean).join(' ')]
    .filter((x) => x && String(x).trim()).join(' · ');

  return (
    <div className="admin-live-map">
      <PageHeader
        title="Live-Karte"
        description="Systemweite Fahrerpositionen, aktive Fahrten und lokale Nachfragesignale."
        actions={
        <div className="admin-map-heading-actions">
          <span className={`admin-map-connection ${socketConnected ? 'is-live' : ''}`}>
            {socketConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            {socketConnected ? 'Live verbunden' : 'Intervall · 8 Sek.'}
          </span>
          <button type="button" className="admin-map-refresh" onClick={erneutVersuchen} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'is-spinning' : ''} /> Aktualisieren
          </button>
        </div>
      }
      />

      {/* Klartext über den Zustand der Karte — bewusst OHNE Fahrer- oder Kundendaten. */}
      {error && (
        <div className="admin-map-stale" role="status">
          <AlertTriangle size={15} /> {error} Die zuletzt bestätigten Positionen bleiben sichtbar.
        </div>
      )}

      {sessionError && (
        <div className="admin-map-stale" role="status">
          <AlertTriangle size={15} /> {sessionError}{' '}
          <a href="/login">Zur Anmeldung</a>
        </div>
      )}

      <section className="admin-map-toolbar" aria-label="Fahrer auf der Karte filtern">
        <div className="admin-map-filters">
          <MapFilter active={filter === 'all'} color="#0a0a0a" icon={Activity} label="Online" value={onlineDrivers.length} onClick={() => setFilter('all')} />
          <MapFilter active={filter === 'free'} color={PHASE_COLOR.free} textColor={PHASE_TEXT.free} icon={Car} label="Frei" value={freeCount} onClick={() => setFilter('free')} />
          <MapFilter active={filter === 'pickup'} color={PHASE_COLOR.pickup} textColor={PHASE_TEXT.pickup} icon={Car} label="Zum Abholort" value={pickupCount} onClick={() => setFilter('pickup')} />
          <MapFilter active={filter === 'pax'} color={PHASE_COLOR.pax} textColor={PHASE_TEXT.pax} icon={Car} label="Mit Fahrgast" value={paxCount} onClick={() => setFilter('pax')} />
        </div>
        <div className="admin-map-toolbar-meta">
          <span><Route size={14} /> <strong>{activeRides.length}</strong> aktive Fahrten</span>
          {presenceSummary && presenceSummary.online && (
            <span data-testid="live-map-presence-summary"
              title={`Pflicht-Tap nach ${presenceSummary.thresholds?.background_min} min App im Hintergrund · Stillstand-Schatten ab ${presenceSummary.thresholds?.still_min} min (zählt nur, stellt nie offline) · Zähler seit Einführung: ${presenceSummary.counters?.still_shadow_flagged_total ?? 0} hätten den Pflicht-Tap ausgelöst, ${presenceSummary.counters?.still_shadow_offline_total ?? 0} wären offline gegangen`}>
              <Activity size={14} /> <strong>{presenceSummary.online.background}</strong> App im Hintergrund · {presenceSummary.online.foreground} vorne · {presenceSummary.online.unknown} unbekannt
              · Schatten {presenceSummary.counters?.still_shadow_flagged_total ?? 0}/{presenceSummary.counters?.still_shadow_offline_total ?? 0}
            </span>
          )}
          <small>{lastUpdate ? `Stand ${uhrzeit(lastUpdate)}` : 'Wird geladen'}</small>
        </div>
      </section>

      <section className="admin-map-layers" aria-label="Kartenebene wählen">
        <span className="admin-map-layers-label"><Layers size={14} /> Ebene</span>
        <div className="admin-map-segment" role="radiogroup" aria-label="Kartenebene">
          {HEAT_LAYERS.map((layer) => (
            <button key={layer.id} type="button" role="radio" aria-checked={heatLayer === layer.id}
              className={heatLayer === layer.id ? 'is-active' : ''} data-testid={`heat-layer-${layer.id}`}
              onClick={() => setHeatLayer(layer.id)}>
              {layer.label}
            </button>
          ))}
        </div>
        {heatLayer === 'demand' && (
          <div className="admin-map-segment is-small" role="radiogroup" aria-label="Zeitfenster der Suchen">
            {SEARCH_WINDOWS.map((minutes) => (
              <button key={minutes} type="button" role="radio" aria-checked={searchWindow === minutes}
                className={searchWindow === minutes ? 'is-active' : ''} data-testid={`search-window-${minutes}`}
                onClick={() => setSearchWindow(minutes)}>
                {minutes} Min.
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Legende NEBEN der Karte (Owner 17.10.2026: sie darf die Karte nicht verdecken). */}
      {!loading && (heatLayer === 'driver' || heatLayer === 'demand') && (
        <div className="admin-map-heat-legend" data-testid="heat-legend">
          <strong>{heatLayer === 'driver' ? 'Fahrer-Heatmap' : `Nachfrage · letzte ${searchWindow} Min.`}</strong>
          {liveHeatError && <span className="admin-map-heat-note is-warning"><AlertTriangle size={13} /> Ebene konnte nicht geladen werden</span>}
          {heatLayer === 'driver' && liveHeat?.driver && !liveHeat.driver.enabled_for_drivers && (
            <span className="admin-map-heat-note is-warning" data-testid="heat-driver-off">Fahrer sehen sie gerade nicht — Schalter in Ultimate V2 ist aus.</span>
          )}
          {heatLayer === 'driver' && liveHeat?.driver?.enabled_for_drivers && (
            <span className="admin-map-heat-note">So sehen freie Fahrer die Karte (ohne ihre persönliche Lücken-Wolke).</span>
          )}
          {heatLayer === 'demand' && demandTotals && (
            <span className="admin-map-heat-note" data-testid="heat-demand-totals">
              {plural(demandTotals.searches, 'Kunde suchte', 'Kunden suchten')} · {demandTotals.booked} gebucht
              {demandTotals.searches > 0 ? ` (${Math.round((demandTotals.booked / demandTotals.searches) * 100)} %)` : ''}
              {` · ${demandTotals.waiting} ${demandTotals.waiting === 1 ? 'wartet' : 'warten'}`}
            </span>
          )}
          {heatLayer === 'demand' && demand?.display?.complete?.[hexResolution] === false && (
            <span className="admin-map-heat-note is-warning" data-testid="heat-demand-limited">Darstellung auf {demand.display.limit} Waben begrenzt.</span>
          )}
          {heatLayer === 'driver' && (
            <ul>
              {[3, 4, 5].map((level) => (
                <li key={level}><i style={{ background: CLOUD_LEVEL[level].fill }} />{CLOUD_LEVEL[level].label}</li>
              ))}
            </ul>
          )}
          {liveHeat && !liveHeatError && heatLayer === 'driver' && cloudZones.length === 0 && (
            <span className="admin-map-heat-note">Gerade keine Wolke.</span>
          )}
        </div>
      )}

      <section className="admin-map-stage">
        <div className="admin-map-surface" ref={surfaceRef}>
          {!loading && (
            <MapContainer center={OPERATING_CENTER} zoom={OPERATING_ZOOM} style={{ height: '100%', width: '100%' }} zoomControl={false}>
              <ZoomControl position="topright" />
              <TileLayer
                url={`https://api.mapbox.com/styles/v1/mapbox/${MAP_STYLE}/tiles/256/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                attribution="&copy; Mapbox &copy; OpenStreetMap"
                tileSize={256}
              />
              <ZoomWatcher onZoom={setZoom} />
              <FlyTo target={flyTarget} />

              {hexes.map((hex) => {
                const color = demandColor(hex.searches);
                if (!color && hex.searches !== 0) return null;
                return (
                  <Polygon
                    key={`hex-${hex.id}`}
                    positions={hex.boundary}
                    pathOptions={{ color: color ? '#ffffff' : '#9ca3af', weight: 1, opacity: 0.9, fillColor: color || '#000000', fillOpacity: color ? 0.66 : 0 }}
                  >
                    <Popup>
                      <div className="admin-map-popup" data-testid="hex-popup">
                        <strong>Nachfrage · letzte {searchWindow} Min.</strong>
                        {hexLines(hex).map((line) => <span key={line}>{line}</span>)}
                      </div>
                    </Popup>
                  </Polygon>
                );
              })}

              {cloudZones.length > 0 && (
                <Pane name="admin-heat-cloud" className="admin-map-heat-pane">
                  {cloudZones.map((zone) => {
                    const look = CLOUD_LEVEL[zone.level];
                    if (!look) return null;
                    return (
                      <Circle
                        key={`cloud-${heatLayer}-${zone.lat}-${zone.lng}`}
                        center={[zone.lat, zone.lng]}
                        radius={CLOUD_RADIUS_M}
                        pathOptions={{ stroke: false, fillColor: look.fill, fillOpacity: look.opacity }}
                      >
                        <Tooltip>
                          <span className="admin-map-cloud-label" data-testid="cloud-zone-label">
                            {look.label}
                          </span>
                        </Tooltip>
                      </Circle>
                    );
                  })}
                </Pane>
              )}

              {activeRides.filter((ride) => !ride.driver_id || driverById[ride.driver_id]?.active_ride_id === ride.id).map((ride) => {
                // Fahrer online auf der Karte → Linie wie beim Marktführer (Auto→Abholort bzw. Auto→Ziel);
                // ohne Fahrer (Suche) oder ohne Position → geplanter Ziel-Abschnitt Abholort→Ziel (grün, gepunktet).
                const driver = ride.driver_id ? driverById[ride.driver_id] : null;
                const onMap = driver && driver.is_online && driver.current_lat && driver.current_lng;
                const geo = onMap ? rideGeometry(ride, { lat: driver.current_lat, lng: driver.current_lng }, driverPhase(driver)) : null;
                const { stops, unreadable } = readRideStops(ride.stops);
                const hasStops = unreadable || stops.length > 0;
                const endpoints = hasStops ? readRideStops([
                  { lat: ride.pickup_lat, lng: ride.pickup_lng },
                  { lat: ride.dropoff_lat, lng: ride.dropoff_lng },
                ]).stops : null;
                // Ohne bestätigten Stoppfortschritt zeigen wir den gesamten gespeicherten Plan.
                // Anfahrt bleibt Auto→Pickup; fehlerhafte Stopps ergeben niemals eine Direktlinie.
                const approach = hasStops && endpoints[0].position && geo && geo.pickup
                  && driverPhase(driver) === 'pickup' && ride.status === 'accepted';
                const line = hasStops ? (approach ? geo.line : null)
                  : geo ? geo.line : [[ride.pickup_lat, ride.pickup_lng], [ride.dropoff_lat, ride.dropoff_lng]];
                const plannedLine = hasStops && !unreadable && stops.every((stop) => stop.position)
                  && endpoints.every((point) => point.position)
                  ? [endpoints[0].position, ...stops.map((stop) => stop.position), endpoints[1].position] : null;
                const selected = !!(ride.driver_id && ride.driver_id === selectedDriverId);
                // Jede Linie ist „zum Abholort" (blau) oder „zum Ziel" (grün) — auch Suche und Zwischenstopp-Plan.
                const lineKind = geo ? geo.kind : 'planned';
                return (
                <React.Fragment key={ride.id}>
                  {line && (
                    <Polyline
                      positions={line}
                      pathOptions={linePathOptions(lineKind, selected)}
                    />
                  )}
                  {plannedLine && (
                    <Polyline
                      positions={plannedLine}
                      pathOptions={linePathOptions('planned', selected)}
                    ><Tooltip>Geplante Strecke mit Zwischenstopps</Tooltip></Polyline>
                  )}
                  {(hasStops ? endpoints[0].position : !geo || geo.pickup) && (
                  <Marker position={[ride.pickup_lat, ride.pickup_lng]} icon={pickupIcon}>
                    <Popup>
                      <div className="admin-map-popup">
                        <strong>{statusLabel[ride.status] || ride.status}</strong>
                        <span><User size={13} /> {ride.customer_name}</span>
                        <span><Car size={13} /> {ride.driver_name || '—'}</span>
                        <span><MapPin size={13} /> {ride.pickup_address?.substring(0, 40)}</span>
                        <b>{ride.price?.toFixed(2)} € · {CLASS_LABELS[ride.ride_type] || ride.ride_type}</b>
                      </div>
                    </Popup>
                  </Marker>
                  )}
                  {stops.filter((stop) => stop.position).map((stop) => (
                    <Marker key={`stop-${stop.number}`} position={stop.position} icon={stopIcon(stop.number)} title={`Zwischenstopp ${stop.number}`}>
                      <Tooltip>Zwischenstopp {stop.number}: {stop.label}</Tooltip>
                      <Popup><strong>Geplanter Zwischenstopp {stop.number}</strong><div>{stop.label}</div></Popup>
                    </Marker>
                  ))}
                  {(hasStops ? endpoints[1].position : !geo || geo.dropoff) && (
                  <Marker position={[ride.dropoff_lat, ride.dropoff_lng]} icon={dropoffIcon}>
                    <Popup>{ride.dropoff_address}</Popup>
                  </Marker>
                  )}
                </React.Fragment>
                );
              })}

              {filteredDrivers.map((driver) => {
                if (!driver.current_lat || !driver.current_lng) return null;
                const phase = driverPhase(driver);
                const selected = driver.id === selectedDriverId;
                return (
                  <GlidingMarker
                    key={driver.id}
                    position={{ lat: driver.current_lat, lng: driver.current_lng }}
                    icon={carIcon(phase, carSize, driver.bearing || 0, selected)}
                    eventHandlers={{ click: () => setSelectedDriverId(driver.id) }}
                  >
                    <Popup>
                      <div className="admin-map-popup">
                        <strong><DriverAvatar name={driver.name} avatar={driver.avatar} size={24} /> {driver.name}</strong>
                        <span className="admin-map-popup-phase" style={{ color: PHASE_TEXT[phase], background: `${PHASE_COLOR[phase]}12` }}>
                          {PHASE_LABEL[phase]}
                        </span>
                        {driver.company_name && <span><Building2 size={13} /> {driver.company_name}</span>}
                        {driver.vehicle_brand && <span><Car size={13} /> {driver.vehicle_brand} {driver.vehicle_model} · {driver.license_plate}</span>}
                        {presenceLabel(driver.presence) && <span>{presenceLabel(driver.presence)}</span>}
                        <small>{driver.current_lat?.toFixed(4)}, {driver.current_lng?.toFixed(4)}</small>
                      </div>
                    </Popup>
                  </GlidingMarker>
                );
              })}
            </MapContainer>
          )}

          {loading && (
            <div className="admin-map-loading">
              <span className="admin-map-loading-dot" />
              <strong>Kartendaten werden geladen</strong>
              <small>Betriebsregion Duisburg/Ruhrgebiet</small>
            </div>
          )}
        </div>

        <aside className="admin-map-rides">
          {/* Owner 17.10.2026: Fahrer und Fahrten stehen NEBEN der Karte (nicht darauf) und als zwei Reiter —
              immer nur einer sichtbar, damit die Spalte nicht zu lang wird. Der andere Reiter bleibt gerendert
              (hidden), Auswahl und Suche bleiben beim Wechsel erhalten. */}
          <div className="admin-map-side-tabs" role="tablist" aria-label="Seitenspalte">
            <button type="button" role="tab" aria-selected={sideTab === 'drivers'} data-testid="side-tab-drivers"
              className={sideTab === 'drivers' ? 'is-active' : ''} onClick={() => setSideTab('drivers')}>
              Fahrer <strong>{onlineDrivers.length}</strong>
            </button>
            <button type="button" role="tab" aria-selected={sideTab === 'rides'} data-testid="side-tab-rides"
              className={sideTab === 'rides' ? 'is-active' : ''} onClick={() => setSideTab('rides')}>
              Aktive Fahrten <strong>{activeRides.length}</strong>
            </button>
          </div>
          {/* Fahrerliste: nur Online-Fahrer, Klick wählt + fliegt hin. */}
          {!loading && (
            <div className="admin-map-drivers" data-testid="live-map-panel" hidden={sideTab !== 'drivers'}>
              <div className="admin-map-drivers-head">
                <div className="admin-map-drivers-search">
                  <Search size={14} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fahrer suchen" aria-label="Fahrer suchen" />
                </div>
                <span className="admin-map-drivers-count">{listedDrivers.length} online</span>
              </div>
              {listOpen && (
                <div className="admin-map-drivers-list" data-testid="live-map-list">
                  {listedDrivers.length === 0 && <div className="admin-map-drivers-empty">Kein Online-Fahrer</div>}
                  {listedDrivers.map((driver) => {
                    const phase = driverPhase(driver);
                    const selected = driver.id === selectedDriverId;
                    const ride = rideByDriver[driver.id];
                    return (
                      <div key={driver.id} className={`admin-map-driver ${selected ? 'is-selected' : ''}`} data-testid="live-map-driver" data-selected={selected ? '1' : '0'}>
                        <div className="admin-map-driver-head" role="button" tabIndex={0}
                          onClick={() => selectDriver(driver)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDriver(driver); } }}>
                          <span className="admin-map-driver-avatar">
                            <DriverAvatar name={driver.name} avatar={driver.avatar} size={36} />
                            <i className="admin-map-driver-dot" style={{ background: PHASE_COLOR[phase] }} aria-label={PHASE_LABEL[phase]} />
                          </span>
                          <div className="admin-map-driver-text">
                            <div className="admin-map-driver-name">{driver.name}</div>
                            {/* LIVEKARTE-FAHRTKETTE-ANZEIGE-001: die Folgefahrt steht unter dem Namen, nicht neben dem Status —
                                in der Statusspalte drückte „Zum Abholort · danach 1 Folgefahrt" den Namen auf „A." zusammen. */}
                            {!!driver.ride_followup_count && (
                              <div className="admin-map-driver-followup" data-testid="live-map-followup-count" style={{ color: PHASE_TEXT[phase] }}>
                                danach {driver.ride_followup_count} Folgefahrt{driver.ride_followup_count === 1 ? '' : 'en'}
                              </div>
                            )}
                            <div className="admin-map-driver-meta">{[driver.company_name, driver.license_plate].filter(Boolean).join(' · ') || 'Ohne Flotte'}</div>
                            {presenceLabel(driver.presence) && (
                              <div className="admin-map-driver-meta" data-testid="live-map-presence">{presenceLabel(driver.presence)}</div>
                            )}
                          </div>
                          <span className="admin-map-driver-phase" style={{ color: PHASE_TEXT[phase] }}>
                            {PHASE_LABEL[phase]}
                          </span>
                        </div>
                        {selected && (
                          <div className="admin-map-driver-detail" data-testid="live-map-driver-detail">
                            <span><Car size={13} /> {vehicleLine(driver) || 'Kein Fahrzeug hinterlegt'}</span>
                            {presenceDetails(driver.presence).map((line) => (
                              <span key={line} data-testid="live-map-presence-detail"><Activity size={13} /> {line}</span>
                            ))}
                            {driver.company_name && <span><Building2 size={13} /> {driver.company_name}</span>}
                            {ride && <RideRouteDetails ride={ride} />}
                            {(followupsByDriver[driver.id] || []).map((followup) => (
                              <div className="admin-map-followup" data-testid="live-map-followup" key={followup.id}>
                                <strong>Danach Folgefahrt</strong>
                                <RideRouteDetails ride={followup} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <button type="button" className="admin-map-drivers-collapse" onClick={() => setListOpen((o) => !o)} aria-label={listOpen ? 'Liste einklappen' : 'Liste ausklappen'} aria-expanded={listOpen}>
                {listOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          )}
          <div className="admin-map-rides-list" data-testid="live-map-rides" hidden={sideTab !== 'rides'}>
            {activeRides.length === 0 ? (
              <div className="admin-map-empty">
                <CheckCircle2 size={22} />
                <strong>Keine aktive Fahrt</strong>
                <small>Neue Fahrten erscheinen automatisch.</small>
              </div>
            ) : activeRides.map((ride) => (
              <ActiveRideCard key={ride.id} ride={ride} selected={!!ride.driver_id && ride.driver_id === selectedDriverId} onSelect={selectRide} />
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
}
